<template>
    <div class="pa-3">
        <v-dialog v-model="dialog" persistent max-width="2000px" >
            <v-card>
              <v-card-title
                ><span class="md-text">Create new products match</span></v-card-title
              >
              <v-card-text class="mt-5">
                <v-row>
                    <v-col 
                      v-for="(productInputTemplate, index) in productInputTemplates" 
                      :key="index" 
                      cols="12" 
                      md="6"
                    >
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Product {{ index + 1 }}</span>
                                <v-spacer></v-spacer>
                                <v-btn 
                                  v-if="productInputTemplates.length > 1"
                                  icon 
                                  small 
                                  @click="removeProduct(index)"
                                  :disabled="isProductInTrackingList(productInputTemplate)"
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </v-card-title>
                            
                            <!-- Domain requirement notice - only show when user entered different domain -->
                            <v-alert
                              v-if="getMainDomainFromTrackingList() && productInputTemplate.mainProductUrl && !isMainDomainConsistent(productInputTemplate.mainProductUrl)"
                              dense
                              outlined
                              type="warning"
                              class="mx-4 mb-3"
                            >
                              <div class="d-flex align-center">
                                <span class="text-caption">
                                  All products must be from <strong>{{ getMainDomainFromTrackingList() }}</strong>. Please change your URL to match this website.
                                </span>
                              </div>
                            </v-alert>
                            
                            <v-card-text>
                                <!-- Product Information Row -->
                                <v-row>
                                  <v-col cols="12" md="8">
                                    <!-- Main Product URL -->
                                    <v-text-field
                                        :label="productInputTemplate.label"
                                        v-model="productInputTemplate.mainProductUrl"
                                        :type="productInputTemplate.type"
                                        :rules="[rules.required, rules.url]"
                                        :error-messages="getUrlErrorMessage(productInputTemplate.mainProductUrl)"
                                        outlined
                                        hide-details
                                    >
                                    <template v-slot:prepend-inner class="pa-0" >
                                        <div class="d-flex align-center justify-center" style="height: 100%;">
                                            <img 
                                              v-if="getFaviconUrl(productInputTemplate.mainProductUrl)"
                                              :src="getFaviconUrl(productInputTemplate.mainProductUrl)"
                                              width="16"
                                              height="16"
                                              class="favicon-icon"
                                              @error="onFaviconError"
                                            />
                                            <v-icon v-else  small color="grey" class="favicon-icon">mdi-link</v-icon>
                                        </div>
                                    </template> 
                                    </v-text-field>
                                  </v-col>
                                  <v-col cols="12" md="4">
                                    <!-- EAN/SKU Input -->
                                    <v-text-field
                                        label="EAN/SKU (optional)"
                                        v-model="productInputTemplate.eanSku"
                                        :disabled="isProductInTrackingList(productInputTemplate)"
                                        outlined
                                        hide-details
                                        placeholder="Enter EAN or SKU"
                                    >
                                    <template v-slot:prepend-inner>
                                        <v-icon small color="grey">mdi-barcode</v-icon>
                                    </template>
                                    </v-text-field>
                                  </v-col>
                                </v-row>
                                
                                <!-- Add button when no matched URLs exist (positioned after main URL) -->
                                <div v-if="productInputTemplate.matchsUrls.length === 0" class="d-flex align-center justify-end mt-4">
                                    <v-tooltip bottom>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                          small 
                                          color="info" 
                                          class="mr-2"
                                          v-bind="attrs" 
                                          v-on="on"
                                        >
                                          mdi-information-outline
                                        </v-icon>
                                      </template>
                                      <span>
                                        Add competitor URLs to track prices across multiple stores.<br>
                                        This helps you monitor price changes and stay competitive.
                                      </span>
                                    </v-tooltip>
                                    
                                    <v-btn 
                                      small 
                                      outlined
                                      color="primary" 
                                      @click="addMatchedUrl(index)"
                                      :disabled="!canAddNewMatchedUrl(index)"
                                      :class="[
                                        'text-none',
                                        { 'animated-button': !isProductInTrackingList(productInputTemplate) }
                                      ]"
                                    >
                                      <v-icon small left>mdi-plus</v-icon>
                                      Add new competitor URL
                                    </v-btn>
                                </div>
                                
                                <!-- Competitor URLs Section Header -->
                                <div v-if="productInputTemplate.matchsUrls.length > 0" class="d-flex align-center justify-between mt-6 mb-2">
                                  <div class="d-flex align-center">
                                    <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
                                    <span class="text-subtitle-2 font-weight-medium">Competitor URLs ({{ productInputTemplate.matchsUrls.length }})</span>
                                  </div>
                                  <v-btn 
                                    icon 
                                    small 
                                    @click="toggleMatchUrlSectionExpanded(index)"
                                    :disabled="isProductInTrackingList(productInputTemplate)"
                                  >
                                    <v-icon small>
                                      {{ (expandedMatchUrlSections[index] !== false && !isProductInTrackingList(productInputTemplate)) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                    </v-icon>
                                  </v-btn>
                                </div>
                                
                                <!-- Matched URLs Section -->
                                <v-expand-transition>
                                  <div v-show="(expandedMatchUrlSections[index] !== false && !isProductInTrackingList(productInputTemplate)) && productInputTemplate.matchsUrls.length > 0" class="ml-6">
                                  <div 
                                    v-for="(matchUrl, urlIndex) in productInputTemplate.matchsUrls" 
                                    :key="urlIndex"
                                    class="mb-3"
                                  >
                                    <v-row no-gutters class="align-center">
                                        <v-text-field
                                          :label="`${matchUrl.label} ${urlIndex + 1}`"
                                          v-model="matchUrl.matchUrl"
                                          :rules="[rules.url]"
                                          :error-messages="getUniqueUrlErrorMessage(index, matchUrl.matchUrl, urlIndex)"
                                          :disabled="isProductInTrackingList(productInputTemplate)"
                                          dense
                                          outlined
                                          placeholder="https://competitor.com/product"
                                          hide-details="auto"
                                          @keydown.enter="handleEnterOnMatchUrl(index, urlIndex, $event)"
                                        >
                                            <template v-slot:prepend>
                                                <v-icon small color="grey">mdi-subdirectory-arrow-right</v-icon>
                                            </template>
                                            <template v-slot:prepend-inner>
                                                <img 
                                                  v-if="getFaviconUrl(matchUrl.matchUrl)"
                                                  :src="getFaviconUrl(matchUrl.matchUrl)"
                                                  width="16"
                                                  height="16"
                                                  class="favicon-icon"
                                                  @error="onFaviconError"
                                                />
                                                <v-icon v-else small color="grey" class="favicon-icon">mdi-link-variant</v-icon>
                                            </template>
                                            <template v-slot:append>
                                                <v-icon 
                                                  medium 
                                                  @click="removeMatchedUrl(index, urlIndex)"
                                                  :disabled="isProductInTrackingList(productInputTemplate)"
                                                  :class="{ 'text--disabled': isProductInTrackingList(productInputTemplate) }"
                                                >
                                                  mdi-close
                                                </v-icon>
                                            </template>
                                        </v-text-field>

                                      <!-- <v-col cols="auto" class="ml-1">
                                      </v-col> -->
                                    </v-row>
                                  </div>
                                  
                                  <!-- Add new competitor URL button when matched URLs exist (positioned after last match URL) -->
                                  <div v-if="productInputTemplate.matchsUrls.length > 0" class="d-flex align-center justify-end mt-4">
                                    <v-tooltip bottom>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                          small 
                                          color="info" 
                                          class="mr-2"
                                          v-bind="attrs" 
                                          v-on="on"
                                        >
                                          mdi-information-outline
                                        </v-icon>
                                      </template>
                                      <span>
                                        Add more competitor URLs to get better price insights.<br>
                                        More competitors = better market coverage!
                                      </span>
                                    </v-tooltip>
                                    
                                    <v-btn 
                                      small 
                                      outlined
                                      color="primary" 
                                      @click="addMatchedUrl(index)"
                                      :disabled="!canAddNewMatchedUrl(index)"
                                      :class="[
                                        'text-none',
                                        { 'animated-button': !isProductInTrackingList(productInputTemplate) }
                                      ]"
                                    >
                                      <v-icon small left>mdi-plus</v-icon>
                                      Add new competitor URL
                                    </v-btn>
                                  </div>
                                </div>
                              </v-expand-transition>
                            </v-card-text>
                            <v-card-actions>
                              <v-btn 
                                medium
                                class="text-none w-100"
                                color="primary" 
                                @click="addToTrackingList(index)"
                                :disabled="!isProductValid(productInputTemplate) || isProductInTrackingList(productInputTemplate) || !isMainDomainConsistent(productInputTemplate.mainProductUrl)"
                              >
                                <v-icon small left>mdi-plus</v-icon>
                                {{ isProductInTrackingList(productInputTemplate) ? 'Added' : 'Add product match' }}
                              </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card outlined @click="addNewProduct" style="cursor: pointer;">
                            <v-card-text class="d-flex justify-center align-center text-center">
                                <div>
                                  <v-icon large color="primary" class="mb-2">mdi-plus</v-icon>
                                  <br>
                                  <span>Add New Product</span>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
              </v-card-text>
              
              <!-- Tracking List Summary Section -->
              <v-divider v-if="trackingList.length > 0"></v-divider>
              <v-card-text v-if="trackingList.length > 0" class="py-3">
                <div class="d-flex align-center mb-2">
                  <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                  <span class="text-subtitle-1 font-weight-medium">Products matchs configured</span>
                  <v-chip small color="primary" class="ml-2">{{ trackingList.length }}</v-chip>
                </div>
                
                <!-- Horizontal scrolling container -->
                <div class="tracking-list-scroll-container">
                  <div class="tracking-list-horizontal d-flex">
                    <div 
                      v-for="(trackingItem, index) in trackingList" 
                      :key="index"
                      class="tracking-item-wrapper flex-shrink-0 mr-3"
                    >
                    <v-card outlined class="tracking-item-card">
                      <v-card-text class="pa-2">
                        <div class="d-flex align-center">
                          <img 
                            v-if="getFaviconUrl(trackingItem.mainProductUrl)"
                            :src="getFaviconUrl(trackingItem.mainProductUrl)"
                            width="20"
                            height="20"
                            class="favicon-icon mr-2"
                            @error="onFaviconError"
                          />
                          <v-icon v-else small color="grey" class="mr-2">mdi-chart-line</v-icon>
                          
                          <div class="flex-grow-1 text-truncate">
                            <div class="text-body-2 font-weight-medium text-truncate">
                              <span class="mr-2">Product {{ index + 1 }}</span><v-chip v-if="trackingItem.eanSku" x-small color="primary" >{{ trackingItem.eanSku }}</v-chip>
                            </div>
                            <div class="text-caption text--secondary text-truncate">
                              {{ extractDomain(trackingItem.mainProductUrl) }}
                            </div>
                            <div class="text-caption text--secondary">
                              + {{ trackingItem.matchsUrls.length }} competitors
                            </div>
                          </div>
                          
                          <!-- Toggle button for matched URLs -->
                          <v-btn 
                            v-if="trackingItem.matchsUrls.length > 0"
                            icon 
                            x-small 
                            @click="toggleTrackingItemExpanded(index)"
                            class="ml-1"
                          >
                            <v-icon x-small>
                              {{ expandedTrackingItems[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                          </v-btn>
                          
                          <v-btn 
                            icon 
                            x-small 
                            @click="removeFromTrackingList(index)"
                            class="ml-1"
                          >
                            <v-icon x-small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        
                        <!-- Expandable matched URLs section -->
                        <v-expand-transition>
                          <div v-if="expandedTrackingItems[index] && trackingItem.matchsUrls.length > 0" class="mt-2 pt-2 border-t">
                            <div class="text-caption text--secondary mb-1 font-weight-medium">
                              Competitor URLs:
                            </div>
                            <div 
                              v-for="(matchUrl, urlIndex) in trackingItem.matchsUrls" 
                              :key="urlIndex"
                              class="d-flex align-center mb-1"
                            >
                              <img 
                                v-if="getFaviconUrl(matchUrl.matchUrl)"
                                :src="getFaviconUrl(matchUrl.matchUrl)"
                                width="14"
                                height="14"
                                class="favicon-icon mr-2"
                                @error="onFaviconError"
                              />
                              <v-icon v-else x-small color="grey" class="mr-2">mdi-link-variant</v-icon>
                              <span class="text-caption text--secondary text-truncate">
                                {{ extractDomain(matchUrl.matchUrl) }}
                              </span>
                            </div>
                          </div>
                        </v-expand-transition>
                      </v-card-text>
                    </v-card>
                    </div>
                  </div>
                </div>
              </v-card-text>
              
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="text-none" color="grey" text @click="dialog = false">Cancel</v-btn>
                <v-btn 
                    class="text-none"
                    color="primary" 
                    @click="startProductTracking"
                    :disabled="trackingList.length === 0 || loading"
                    :loading="loading"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Start Tracking {{ trackingList.length }} Product{{ trackingList.length !== 1 ? 's' : '' }} </v-btn>
              </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- button to show the modal-->
        <!-- <v-btn color="primary" @click="dialog = true">Import Product</v-btn> -->
    </div>
</template>
<script>
import axios from 'axios'
export default {
  name: "ImportSingleProduct",
  data() {
    return {
      loading: false,
      dialog: false,
      productInputTemplates:[],
      trackingList: [], // List to store products before adding to price tracking
      expandedTrackingItems: {}, // Track which tracking items are expanded to show matched URLs
      expandedMatchUrlSections: {}, // Track which product input sections have expanded matched URLs
      matchUrlTemplate:{
        matchUrl:'',
        label:'Enter product URL',
        type:'text',
        required:false,
      },
      rules: {
        required: value => !!value || 'This field is required.',
        url: value => {
          if (!value) return true; // Allow empty for optional fields
          const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
          return urlPattern.test(value) || 'Please enter a valid URL (e.g., https://example.com)';
        }
      }
    };
  },
  created() {
    const productInputTemplate = {
        mainProductUrl:'',
        eanSku: '', // EAN/SKU field
        label: 'Enter main product URL',
        type: 'text',
        required: true,
        metaData:{
            brand:'',
            category:'',
            ean:'',
            sku:'',
            mpn:''
        },
        matchsUrls:[]
    }
    this.productInputTemplates.push(productInputTemplate);
  },
  methods: {
    toggleTrackingItemExpanded(index) {
      this.$set(this.expandedTrackingItems, index, !this.expandedTrackingItems[index]);
    },
    
    toggleMatchUrlSectionExpanded(index) {
      this.$set(this.expandedMatchUrlSections, index, !this.expandedMatchUrlSections[index]);
    },
    
    canAddNewMatchedUrl(productIndex) {
      const product = this.productInputTemplates[productIndex];
      
      // Don't allow if product is already in tracking list
      if (this.isProductInTrackingList(product)) {
        return false;
      }
      
      // Don't allow if main product URL is not filled and valid
      if (!product.mainProductUrl || !this.isValidUrl(product.mainProductUrl)) {
        return false;
      }
      
      // Check if all existing competitor URLs are filled, valid, and unique
      return product.matchsUrls.every((matchUrl, index) => {
        const url = matchUrl.matchUrl && matchUrl.matchUrl.trim();
        return url && 
               this.isValidUrl(url) &&
               !this.isDuplicateDomain(productIndex, url, index);
      });
    },

    isDuplicateDomain(productIndex, url, excludeIndex = -1) {
      const product = this.productInputTemplates[productIndex];
      if (!url || !this.isValidUrl(url)) return false;
      
      const currentDomain = this.extractDomain(url);
      const mainDomain = this.extractDomain(product.mainProductUrl);
      
      // Check against main product URL domain
      if (mainDomain === currentDomain) {
        return true;
      }
      
      // Check against other competitor URLs
      return product.matchsUrls.some((matchUrl, index) => {
        if (index === excludeIndex) return false; // Exclude current field when editing
        if (!matchUrl.matchUrl || !this.isValidUrl(matchUrl.matchUrl)) return false;
        
        const matchDomain = this.extractDomain(matchUrl.matchUrl);
        return matchDomain === currentDomain;
      });
    },

    getUniqueUrlErrorMessage(productIndex, url, urlIndex) {
      if (!url) return '';
      
      if (!this.isValidUrl(url)) {
        return 'Please enter a valid URL';
      }
      
      if (this.isDuplicateDomain(productIndex, url, urlIndex)) {
        return 'This website is already added. Please use a different website.';
      }
      
      return '';
    },
    
    getFaviconUrl(url) {
      if (!url || !this.isValidUrl(url)) return null;
      
      try {
        // Extract domain from URL
        let domain = url;
        
        // Add protocol if missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          domain = 'https://' + url;
        }
        
        const urlObj = new URL(domain);
        const hostname = urlObj.hostname;
        
        // Use Google's favicon service
        return `https://www.google.com/s2/favicons?domain=${hostname}&sz=30`;
      } catch (error) {
        return null;
      }
    },
    
    onFaviconError(event) {
      // Hide the favicon image if it fails to load
      event.target.style.display = 'none';
    },
    
    getUrlErrorMessage(url) {
      if (!url) return [];
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      if (!urlPattern.test(url)) {
        return ['Please enter a valid URL (e.g., https://example.com)'];
      }
      return [];
    },
    
    isValidUrl(url) {
      if (!url) return false;
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      return urlPattern.test(url);
    },
    
    validateAllUrls() {
      let isValid = true;
      
      this.productInputTemplates.forEach(template => {
        // Validate main product URL
        if (template.required && !template.mainProductUrl) {
          isValid = false;
        }
        if (template.mainProductUrl && !this.isValidUrl(template.mainProductUrl)) {
          isValid = false;
        }
        
        // Validate match URLs
        template.matchsUrls.forEach(matchUrl => {
          if (matchUrl.matchUrl && !this.isValidUrl(matchUrl.matchUrl)) {
            isValid = false;
          }
        });
      });
      
      return isValid;
    },
    
    handleImport() {
      if (!this.validateAllUrls()) {
        this.$emit('show-notification', {
          msg: 'Please fix all URL validation errors before importing.',
          type: 'error'
        });
        return;
      }
      
      // Show success message for now
      this.$emit('show-notification', {
        msg: 'Products imported successfully!',
        type: 'success'
      });
      
      this.dialog = false;
    },
    
    addNewProduct() {
      const newProduct = {
        mainProductUrl: '',
        eanSku: '', // EAN/SKU field
        label: 'Enter main product URL',
        type: 'text',
        required: true,
        metaData: {
          brand: '',
          category: '',
          ean: '',
          sku: '',
          mpn: ''
        },
        matchsUrls: []
      };
      this.productInputTemplates.push(newProduct);
    },
    
    removeProduct(index) {
      if (this.productInputTemplates.length > 1) {
        this.productInputTemplates.splice(index, 1);
      }
    },
    
    addMatchedUrl(productIndex) {
      // Check if we can add a new matched URL
      if (!this.canAddNewMatchedUrl(productIndex)) {
        // Show notification about why they can't add more URLs
        const product = this.productInputTemplates[productIndex];
        
        if (!product.mainProductUrl || !this.isValidUrl(product.mainProductUrl)) {
          this.$emit('show-notification', {
            msg: 'Please enter a valid main product URL first',
            type: 'warning'
          });
        } else if (product.matchsUrls.some(matchUrl => !matchUrl.matchUrl || !this.isValidUrl(matchUrl.matchUrl))) {
          this.$emit('show-notification', {
            msg: 'Please fill and validate all existing competitor URLs first',
            type: 'warning'
          });
        } else if (product.matchsUrls.some((matchUrl, index) => this.isDuplicateDomain(productIndex, matchUrl.matchUrl, index))) {
          this.$emit('show-notification', {
            msg: 'Please remove duplicate websites before adding new ones',
            type: 'warning'
          });
        }
        return;
      }
      
      this.productInputTemplates[productIndex].matchsUrls.push({
        matchUrl: '',
        label: 'Enter product URL',
        type: 'text',
        required: false,
      });
      
      // Ensure the matched URLs section is expanded when adding new URLs
      if (this.expandedMatchUrlSections[productIndex] === false) {
        this.$set(this.expandedMatchUrlSections, productIndex, true);
      }
    },
    
    handleEnterOnMatchUrl(productIndex, urlIndex, event) {
      // Prevent default form submission
      event.preventDefault();
      
      // Don't allow adding new fields if product is already in tracking list
      if (this.isProductInTrackingList(this.productInputTemplates[productIndex])) {
        return;
      }
      
      const currentMatchUrl = this.productInputTemplates[productIndex].matchsUrls[urlIndex];
      const currentUrl = currentMatchUrl.matchUrl;
      
      // Only proceed if the current URL is valid and not empty
      if (currentUrl && this.isValidUrl(currentUrl)) {
        // Check if this is the last matched URL in the list
        const isLastUrl = urlIndex === this.productInputTemplates[productIndex].matchsUrls.length - 1;
        
        if (isLastUrl) {
          // Add a new matched URL field
          this.addMatchedUrl(productIndex);
          
          // Focus on the newly created field after a short delay
          this.$nextTick(() => {
            // Find all text field inputs in this product
            const productCard = event.target.closest('.v-card');
            if (productCard) {
              const textFields = productCard.querySelectorAll('.v-text-field input');
              const newFieldIndex = textFields.length - 1;
              if (textFields[newFieldIndex]) {
                textFields[newFieldIndex].focus();
              }
            }
          });
        } else {
          // Focus on the next existing field
          this.$nextTick(() => {
            const productCard = event.target.closest('.v-card');
            if (productCard) {
              const textFields = productCard.querySelectorAll('.v-text-field input');
              // Calculate the index of the next matched URL field
              // Main URL is index 0, so matched URLs start from index 1
              const nextFieldIndex = urlIndex + 2; // +1 for next URL, +1 because main URL is at index 0
              if (textFields[nextFieldIndex]) {
                textFields[nextFieldIndex].focus();
              }
            }
          });
        }
      }
    },
    
    removeMatchedUrl(productIndex, urlIndex) {
      this.productInputTemplates[productIndex].matchsUrls.splice(urlIndex, 1);
    },
    
    isProductValid(product) {
      // Check if main URL is provided and valid
      if (!product.mainProductUrl) return false;
      if (!this.isValidUrl(product.mainProductUrl)) return false;
      
      // Check matched URLs (only non-empty ones need to be valid)
      return product.matchsUrls.every(matchUrl => {
        if (!matchUrl.matchUrl) return true; // Empty URLs are allowed
        return this.isValidUrl(matchUrl.matchUrl);
      });
    },

    getMainDomainFromTrackingList() {
      if (this.trackingList.length === 0) return null;
      return this.extractDomain(this.trackingList[0].mainProductUrl);
    },

    isMainDomainConsistent(productUrl) {
      const existingDomain = this.getMainDomainFromTrackingList();
      if (!existingDomain) return true; // First product, no constraint
      
      const newDomain = this.extractDomain(productUrl);
      return existingDomain === newDomain;
    },

    addToTrackingList(productIndex) {
      const product = this.productInputTemplates[productIndex];
      if (!this.isProductValid(product) || this.isProductInTrackingList(product)) {
        return;
      }

      // Check if main domain is consistent with existing tracking list
      if (!this.isMainDomainConsistent(product.mainProductUrl)) {
        const existingDomain = this.getMainDomainFromTrackingList();
        this.$emit('show-notification', {
          msg: `All products must be from the same main website (${existingDomain}). Please use a URL from ${existingDomain} instead.`,
          type: 'error'
        });
        return;
      }
      
      // Filter matched URLs to include only validated ones
      const validMatchedUrls = product.matchsUrls.filter(matchUrl => {
        return matchUrl.matchUrl && 
               matchUrl.matchUrl.trim() !== '' && 
               this.isValidUrl(matchUrl.matchUrl.trim());
      });
      
      // Create a copy of the product for the tracking list with filtered URLs
      const trackingItem = {
        ...product,
        matchsUrls: validMatchedUrls,
        addedAt: new Date()
      };
      
      this.trackingList.push(trackingItem);
      
      // Automatically collapse the matched URLs section when added to tracking list
      this.$set(this.expandedMatchUrlSections, productIndex, false);
      
      // Show success message with count of valid URLs
      const totalValidUrls = validMatchedUrls.length;
      const urlText = totalValidUrls === 1 ? 'competitor URL' : 'competitor URLs';
      
      this.$emit('show-notification', {
        msg: `Product ${productIndex + 1} added to tracking list successfully! (${totalValidUrls} ${urlText} included)`,
        type: 'success'
      });
    },
    
    removeFromTrackingList(trackingIndex) {
      const removedItem = this.trackingList[trackingIndex];
      this.trackingList.splice(trackingIndex, 1);
      
      // Find the corresponding product in productInputTemplates and expand its matched URLs section
      const productIndex = this.productInputTemplates.findIndex(product => 
        product.mainProductUrl === removedItem.mainProductUrl
      );
      
      if (productIndex !== -1 && this.productInputTemplates[productIndex].matchsUrls.length > 0) {
        // Automatically expand the matched URLs section when removed from tracking list
        this.$set(this.expandedMatchUrlSections, productIndex, true);
      }
    },
    
    isProductInTrackingList(product) {
      return this.trackingList.some(trackingItem => 
        trackingItem.mainProductUrl === product.mainProductUrl
      );
    },
    
    extractDomain(url) {
      try {
        let domain = url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          domain = 'https://' + url;
        }
        const urlObj = new URL(domain);
        return urlObj.hostname;
      } catch (error) {
        return url;
      }
    },
    
    async startProductTracking() {
      if (this.trackingList.length === 0) return;
      
      this.loading = true;
      
      try {
        // Prepare data for API
        const data = this.trackingList.map(product => {
          // Get validated competitor URLs
          const validCompetitorUrls = product.matchsUrls
            .filter(matchUrl => matchUrl.matchUrl && 
                               matchUrl.matchUrl.trim() !== '' && 
                               this.isValidUrl(matchUrl.matchUrl.trim()))
            .map(matchUrl => matchUrl.matchUrl.trim());
          
          // Combine main URL and validated matched URLs
          const allUrls = [product.mainProductUrl, ...validCompetitorUrls];
          
          return {
            ref: product.eanSku || product.metaData?.sku || '',
            ean: product.eanSku || product.metaData?.ean || '',
            model: product.metaData?.model || '',
            brand: product.metaData?.brand || '',
            category: product.metaData?.category || '',
            cost: 0.0,
            urls: allUrls.map(url => ({ url: url.trim() })),
            id: 'TRACK_' + Date.now() + '_' + Math.random()
          };
        });

        const params = {
          account_id: localStorage.getItem('account_id'),
          user_id: localStorage.getItem('user_id'),
          products: data,
          autoDiscovery: true,
        }

        axios.post('/api/importation/addproduts', {
          params: params
        }, {
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(() => {
          this.$emit('show-notification', {
            msg: 'The importation request has been successfully uploaded, it will be processed soon',
            type: 'success',
          })
          
          // Emit event to refresh importations list
          this.$emit('import-success')
        })
        
        //clear the product input fields
        this.productInputTemplates = [];
        const productInputTemplate = {
            mainProductUrl:'',
            eanSku: '', // EAN/SKU field
            label: 'Enter main product URL',
            type: 'text',
            required: true,
            metaData:{
                brand:'',
                category:'',
                ean:'',
                sku:'',
                mpn:''
            },
            matchsUrls:[]
        }
        this.productInputTemplates.push(productInputTemplate);
        
        // Clear tracking list and close dialog
        this.trackingList = [];
        this.dialog = false;
        
      } catch (error) {
        console.error('Tracking setup error:', error);
        this.$emit('show-notification', {
          msg: 'An error occurred while setting up price tracking. Please try again.',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.compact-input {
  font-size: 0.875rem;
}

.compact-input >>> .v-input__control {
  min-height: 36px !important;
}

.compact-input >>> .v-text-field__details {
  margin-top: 2px;
  padding-top: 0;
}

.compact-input >>> .v-input__slot {
  min-height: 36px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.favicon-icon {
  border-radius: 2px;
  object-fit: contain;
  margin-top:4px;
  margin-right: 4px;
}

.tracking-item-card {
  transition: all 0.2s ease;
}

.tracking-item-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Animated button with subtle color cycling */
.animated-button {
  animation: color-pulse 3s ease-in-out infinite;
  transition: all 0.3s ease;
}

.animated-button:hover {
  animation-play-state: paused;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.12);
}

@keyframes color-pulse {
  0%, 100% {
    border-color: #1976d2; /* Primary blue */
    color: #1976d2;
  }
  25% {
    border-color: #00bcd4; /* Cyan */
    color: #00bcd4;
  }
  50% {
    border-color: #4caf50; /* Green */
    color: #4caf50;
  }
  75% {
    border-color: #ff9800; /* Orange */
    color: #ff9800;
  }
}

/* Info icon subtle pulse */
.v-icon.mdi-information-outline {
  animation: info-glow 2s ease-in-out infinite alternate;
}

@keyframes info-glow {
  from {
    opacity: 0.7;
  }
  to {
    opacity: 1;
  }
}

/* Horizontal scrolling for tracking list */
.tracking-list-scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.tracking-list-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.tracking-list-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tracking-list-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.tracking-list-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.tracking-list-horizontal {
  min-width: fit-content;
}

.tracking-item-wrapper {
  width: 280px;
  min-width: 280px;
}

.tracking-item-wrapper:last-child {
  margin-right: 0 !important;
}
</style>
